import {DateTime} from "luxon";
import {html, HTMLTemplateResult, nothing, PropertyValues} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {state} from "lit/decorators/state.js";
import {classMap} from "lit/directives/class-map.js";
import {get} from "lit-translate";
import {container} from "tsyringe";

import {KnowledgeObject} from "../../../domain/entities/knowledge-object.entity";
import {EmployeeRepository} from "../../../domain/repositories/employee.repository";
import {KnowledgeObjectRepository} from "../../../domain/repositories/knowledge-object.repository";
import {DateHelper} from "../../../helpers/date.helper";
import {DialogHelper} from "../../../helpers/dialog.helper";
import {UiHelper} from "../../../helpers/ui.helper";
import {GlobalNotificationService} from "../../../services/global-notification.service";
import {AbstractLitElement} from "../../abstract-lit.element";
import {deleteKnowledgeObject} from "./knowledge-object-delete";

// Maps KnowledgeObject.type → icon class (see font_awesome.min.css). Extend when new types ship.
const TYPE_ICON_CLASS: Readonly<Record<string, string>> = {
    whiteboard: 'icon-chalkboard',
    document: 'icon-file-lines',
    image: 'icon-image',
};
const DEFAULT_TYPE_ICON_CLASS = 'icon-file';

/** One object that embeds this row's object — what the hover panel lists. */
export interface KnowledgeObjectReference {
    readonly title: string;
    readonly type: string;
}

/** i18n namespace of every user-facing string in this row. */
const I18N_ROW_MENU = 'wissen.overview.list.row-menu';

/** i18n namespace of the column labels (dates/creator/editor) reused for the
 *  row's tooltip (Stefan, 2026-08-30) — these keys already existed, prepared
 *  for a table layout that was never built. `col-title` is a sibling key in
 *  the same namespace that WAS briefly used here too (the tooltip's title
 *  line originally carried a "Titel:" prefix) but is unused as of the
 *  2026-08-30 follow-up that dropped that prefix ("das ist nicht nötig") —
 *  left in `de.json` on purpose, for whichever UI (this tooltip or a future
 *  table view) ends up wanting a labeled title again, not because it was
 *  always dormant. */
const I18N_LIST = 'wissen.overview.list';

/**
 * Single-row renderer for a KnowledgeObject in the virtualized overview list.
 *
 * Stateless regarding data access — `item` is bound by the parent
 * `knowledge-overview-view`. Click emits a bubbling `knowledge-object-selected`
 * CustomEvent that the parent uses to update selection + URL.
 *
 * Light DOM is inherited from AbstractLitElement (ADR-007).
 */
@customElement('knowledge-object-item')
export class KnowledgeObjectItem extends AbstractLitElement {

    @property({type: Object})
    public item: KnowledgeObject | null = null;

    @property({type: Boolean})
    public selected = false;

    /**
     * Whether this object currently has an unsaved local draft (Anke,
     * 2026-09-16) — computed by the parent list via `hasKnowledgeDraft()`
     * (a plain localStorage read) and passed in rather than looked up here,
     * since this row is inside a virtualized list re-rendered on scroll;
     * see `knowledge-sidebar-list.component.ts`'s own `.hasDraft=` binding
     * and its `KnowledgeDraftChangeNotifier` subscription for the live
     * update signal.
     */
    @property({type: Boolean})
    public hasDraft = false;

    /**
     * The objects that embed this one, or null when the question does not apply
     * to this row.
     *
     * Null and an empty array mean different things and must stay apart: null is
     * "not asked" (every row that is not an image inside the assets folder), an
     * empty array is the measured answer "used nowhere". Collapsing them would
     * show a 0 on every ordinary row.
     */
    @property({type: Array})
    public references: readonly KnowledgeObjectReference[] | null = null;

    /** Whether the hover panel listing the referencing objects is open. */
    @state()
    private _referencesOpen = false;

    /** Re-entrancy guard for the async row-menu handler. */
    private _isProcessingClick = false;

    /**
     * Resolved creator/last-editor display names, backing the row's native
     * `title` tooltip (Stefan, 2026-08-30: "titel und daten und ersteller
     * auch im tooltip angezeigt"). Empty until resolved (or when the item
     * has no creator/editor uid) — the tooltip simply omits that line rather
     * than showing a placeholder.
     *
     * Loaded ONCE per item (`willUpdate` below), not on every render — this
     * also replaces the previous per-render `EmployeeRepository.findById()`
     * call the visible "last editor" meta text used to make via `until()`:
     * the row is inside a virtualized list, so a per-render fetch would have
     * re-fired on every scroll-triggered re-render.
     */
    @state()
    private _creatorName = '';

    @state()
    private _editorName = '';

    /**
     * Track item identity across willUpdate calls to skip redundant loads.
     *
     * NOT a virtualizer-recycling guard (review 01, 2026-08-30, WARNING,
     * Linse A/C — an earlier version of this comment claimed otherwise):
     * `knowledge-sidebar-list`'s `virtualize(...)` call uses no explicit
     * `keyFunction` (default: item identity) AND `_buildDisplayRows()` builds
     * a FRESH `{kind:'object', item}` wrapper on every host render, so
     * `@lit-labs/virtualizer`/`repeat()` tears down and re-CONSTRUCTS a new
     * `<knowledge-object-item>` for a different row rather than reusing one
     * instance across items. What this guard actually protects against is a
     * re-render of the SAME instance for the SAME item (e.g. the `selected`
     * property toggling) re-triggering the async lookup for no reason — see
     * the "does not re-fetch ... for the SAME item" test.
     *
     * Set SYNCHRONOUSLY before the async lookup starts (unlike
     * knowledge-image.component.ts's `_lastItemUid`, which is set only after
     * the load settles) — a second `willUpdate` for the same item that
     * arrives WHILE the first lookup is still in flight is deduped
     * immediately either way, since `_loadGeneration` below governs which
     * result is authoritative once it resolves.
     */
    private _lastItemUid: string | null = null;

    /**
     * Monotonic counter incremented on every genuinely new `item`. Each
     * in-flight name-resolution captures the value at dispatch time and
     * discards its result if the counter has moved on — same stale-promise
     * guard as knowledge-image.component.ts's `_loadGeneration`.
     */
    private _loadGeneration = 0;

    public willUpdate(changedProperties: PropertyValues): void {
        const item = this.item;
        if (!item) {
            this._creatorName = '';
            this._editorName = '';
            this._lastItemUid = null;
            return;
        }
        if (!changedProperties.has('item') || item.uid === this._lastItemUid) {
            return;
        }
        this._lastItemUid = item.uid;
        this._creatorName = '';
        this._editorName = '';

        this._loadGeneration += 1;
        const myGeneration = this._loadGeneration;
        void this._resolveNames(item, myGeneration);
    }

    /** Resolves creator + last-editor names in ONE batched lookup (both uids
     *  may be identical — self-created, never re-edited by someone else —
     *  `findById` dedupes internally either way). */
    private async _resolveNames(item: KnowledgeObject, myGeneration: number): Promise<void> {
        const uids = [item.creatorUid, item.lastEditorUid].filter((uid): uid is string => !!uid);
        if (uids.length === 0) {
            return;
        }
        try {
            const employees = await container.resolve(EmployeeRepository).findById(uids);
            if (myGeneration !== this._loadGeneration) {
                return; // A newer load has been kicked off — discard this stale result.
            }
            const byUid = (uid: string): string =>
                employees.find(employee => employee.uid === uid)?.name ?? '';
            this._creatorName = item.creatorUid ? byUid(item.creatorUid) : '';
            this._editorName = item.lastEditorUid ? byUid(item.lastEditorUid) : '';
        } catch (error) {
            if (myGeneration !== this._loadGeneration) {
                return;
            }
            console.error(`Failed to resolve creator/editor names for knowledge object «${item.uid}».`, error);
        }
    }

    /**
     * Full-detail tooltip (native `title` attribute) for the whole row —
     * title, created/updated dates, and creator/last-editor when resolved.
     * Reuses the `col-*` i18n labels already prepared under
     * `wissen.overview.list` (a table layout that was never built) so no new
     * translation keys were needed. The title itself is the first line, on
     * its own, WITHOUT a "Titel:"/`col-title` label prefix (Stefan,
     * 2026-08-30: "das wort 'Titel:' vor dem dateinamen ... weglassen. das
     * ist nicht nötig") — every other line keeps its label, since only the
     * title is unambiguous without one (it's the row's own name, right above
     * the dated/attributed lines).
     *
     * Native `title`, NOT the `_renderReferences()` custom hover-panel
     * pattern below (review 01, 2026-08-30, MINOR, Linse B): the content
     * here is plain multi-line text with no icons/structure to lay out, so a
     * native tooltip is the simpler fit — the references panel earns its
     * custom `role="tooltip"` div because it renders a real list (icon +
     * title per entry), which `title` cannot express at all.
     */
    private _buildTooltip(item: KnowledgeObject): string {
        const lines = [
            item.title,
            `${get(`${I18N_LIST}.col-created-at`)}: ${this._formatDateText(item.createdAt)}`,
        ];
        if (this._creatorName) {
            lines.push(`${get(`${I18N_LIST}.col-creator`)}: ${this._creatorName}`);
        }
        lines.push(`${get(`${I18N_LIST}.col-updated-at`)}: ${this._formatDateText(item.updatedAt)}`);
        if (this._editorName) {
            lines.push(`${get(`${I18N_LIST}.col-last-editor`)}: ${this._editorName}`);
        }
        return lines.join('\n');
    }

    protected render(): HTMLTemplateResult | typeof nothing {
        const item = this.item;
        if (!item) {
            return nothing;
        }

        const iconClass = TYPE_ICON_CLASS[item.type] ?? DEFAULT_TYPE_ICON_CLASS;
        const actionsLabel = get(`${I18N_ROW_MENU}.actions`);

        // `item.uid` is a Base64URL ShortId — used directly as `data-sid` per ADR-001.
        // `title` is the row's full-detail tooltip (Stefan, 2026-08-30) — see
        // `_buildTooltip`. On the outer row so hovering ANYWHERE on the row
        // (not just the truncated title text) shows it.
        return html`
            <div data-sid=${item.uid}
                 class=${classMap({'knowledge-object-item': true, selected: this.selected})}
                 title=${this._buildTooltip(item)}
                 @click=${() => this._onClick(item.uid)}>
                <div class="knowledge-object-item__icon-cell">
                    <i class="icon ${iconClass}" aria-hidden="true"></i>
                </div>
                <div class="knowledge-object-item__main-cell">
                    <div class="knowledge-object-item__title ${this.hasDraft ? 'knowledge-object-item__title--unsaved' : ''}"
                    >${item.title}${this.hasDraft ? html`<sup>*</sup>` : nothing}</div>
                    <div class="knowledge-object-item__meta">
                        <span class="knowledge-object-item__updated-at">${this._formatDate(item.updatedAt)}</span>
                        <span class="knowledge-object-item__editor">${this._editorName}</span>
                    </div>
                </div>
                ${this._renderReferences()}
                <div class="knowledge-object-item__menu-cell"
                     @click=${(e: Event) => this._onMenuClick(e, item)}>
                    <i class="icon icon-ellipsis-vertical knowledge-object-item__menu-icon"
                       aria-label=${actionsLabel}
                       title=${actionsLabel}></i>
                    <!-- This nested title attribute intentionally shadows the
                         outer row's tooltip while hovering the menu icon —
                         browsers reliably show the INNERMOST title in a
                         nesting, so the icon's own (shorter, action-specific)
                         tooltip wins here without any extra wiring (review
                         01, 2026-08-30, MINOR, Linse B). -->
                </div>
            </div>
        `;
    }

    /**
     * How many objects embed this one, with the list of them on hover.
     *
     * The cell is rendered whenever the question applies, EMPTY COUNT INCLUDED —
     * a zero is an answer ("used nowhere"), and it also keeps the column the same
     * width for every row instead of shifting the menu icon around. Rows that
     * were never asked (`references === null`) render nothing at all.
     *
     * The list is read straight from the property. Nothing is fetched here: this
     * runs inside a virtualized list on every scroll and re-render, so a request
     * in here would fire continuously.
     */
    private _renderReferences(): HTMLTemplateResult | typeof nothing {
        const references = this.references;
        if (references === null) {
            return nothing;
        }

        return html`
            <div class="knowledge-object-item__references-cell"
                 @mouseenter=${() => { this._referencesOpen = references.length > 0; }}
                 @mouseleave=${() => { this._referencesOpen = false; }}
                 @click=${(event: Event) => event.stopPropagation()}>
                <span class="knowledge-object-item__references-count">${references.length}</span>
                ${this._referencesOpen ? html`
                    <div class="knowledge-object-item__references-panel" role="tooltip">
                        ${references.map(reference => html`
                            <div class="knowledge-object-item__references-entry">
                                <i class="icon ${TYPE_ICON_CLASS[reference.type] ?? DEFAULT_TYPE_ICON_CLASS}"
                                   aria-hidden="true"></i>
                                <span>${reference.title}</span>
                            </div>
                        `)}
                    </div>
                ` : nothing}
            </div>
        `;
    }

    /**
     * Opens the row action menu. `stopPropagation` is kept so opening the menu
     * never selects the row underneath it.
     *
     * Re-entrancy guard + try/catch are mandatory for async Lit handlers: a
     * second click would otherwise open a second menu and dispatch a second
     * delete, and a rejected dispatch would surface as an unhandled rejection
     * with no user feedback at all.
     */
    private async _onMenuClick(event: Event, item: KnowledgeObject): Promise<void> {
        event.stopPropagation();

        if (this._isProcessingClick) {
            return;
        }
        this._isProcessingClick = true;

        try {
            const target = (event.currentTarget ?? event.target) as HTMLElement;
            const selected = await UiHelper.showContextMenu(target, [
                {id: 'rename', label: get(`${I18N_ROW_MENU}.rename`), type: 'item'},
                {id: 'delete', label: get(`${I18N_ROW_MENU}.delete`), type: 'item'},
            ]);

            if (selected?.id === 'rename') {
                this._onRename(item);
                return;
            }

            if (selected?.id !== 'delete') {
                return;
            }

            await deleteKnowledgeObject(item, {
                confirm: knowledgeObject => UiHelper.confirm({
                    title: get(`${I18N_ROW_MENU}.confirm-title`),
                    // The object's name is an interpolation value, not a
                    // concatenation — the placeholder's position differs per
                    // language and must stay under the translator's control.
                    content: html`<p>${get(`${I18N_ROW_MENU}.confirm-content`, {title: knowledgeObject.title})}</p>`,
                    confirmLabel: get(`${I18N_ROW_MENU}.confirm-submit`),
                    cancelLabel: get(`${I18N_ROW_MENU}.confirm-cancel`),
                }),
                dispatch: knowledgeObject => this._dispatchDelete(knowledgeObject),
            });
        } catch (error) {
            console.error(`Failed to delete knowledge object «${item.uid}».`, error);
            container.resolve(GlobalNotificationService).error(
                get(`${I18N_ROW_MENU}.error-delete`),
                error instanceof Error ? error.message : undefined,
            );
        } finally {
            // Released in `finally`, not on the happy path — a guard kept after
            // a failure would dead-lock the menu for the rest of the session.
            this._isProcessingClick = false;
        }
    }

    /**
     * Deletes through the repository rather than resolving the command off the
     * CommandBus here. Two delete paths would diverge: the repository throws on
     * a rejected dispatch, so only this route surfaces a failed delete instead
     * of silently leaving the row looking removed.
     */
    private async _dispatchDelete(knowledgeObject: KnowledgeObject): Promise<void> {
        await container.resolve(KnowledgeObjectRepository).delete(knowledgeObject);
    }

    /**
     * Opens the shared edit dialog in rename mode. Moved here from the whiteboard
     * toolbar's own Rename button (Anke, 2026-08-17) — same shared edit dialog,
     * opened from the row instead of from inside an already-open whiteboard.
     *
     * Own try/catch (not folded into the outer delete flow's) so a synchronous
     * throw from `DialogHelper.showDialog()` (e.g. `ha-dialog-knowledge-object-edit`
     * not yet registered as a custom element) surfaces a rename-specific error
     * instead of the misleading "Löschen fehlgeschlagen" notification the shared
     * catch block would have produced (review 01, MINOR, Linse B, 2026-08-18).
     */
    private _onRename(item: KnowledgeObject): void {
        try {
            DialogHelper.showDialog('ha-dialog-knowledge-object-edit', {knowledgeObject: item});
        } catch (error) {
            console.error(`Failed to open rename dialog for knowledge object «${item.uid}».`, error);
            container.resolve(GlobalNotificationService).error(
                get(`${I18N_ROW_MENU}.error-rename`),
                error instanceof Error ? error.message : undefined,
            );
        }
    }

    /** Emits a bubbling `knowledge-object-selected` event with the entity's ShortId. */
    private _onClick(sid: string): void {
        this.dispatchEvent(new CustomEvent('knowledge-object-selected', {
            detail: {sid},
            bubbles: true,
            composed: true,
        }));
    }

    private _formatDate(value: DateTime | null): string | typeof nothing {
        if (!(value instanceof DateTime)) {
            return nothing;
        }
        return DateHelper.fromDateTimeToDateTimeString(value);
    }

    /** Same formatting as `_formatDate`, but for the tooltip's plain-string
     *  context — a `title` attribute can't hold the `nothing` Lit sentinel. */
    private _formatDateText(value: DateTime | null): string {
        const formatted = this._formatDate(value);
        return formatted === nothing ? '' : formatted;
    }
}
